import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  BadgeCheck,
  Car,
  CheckCircle2,
  CircleX,
  Loader2,
  Lock,
  MessageCircle,
  Package,
  ShieldCheck,
  Smartphone,
  Truck,
} from "lucide-react";
import {
  BentoCard,
  GhostButton,
  ImgPlaceholder,
  RydeCta,
  RydeFade,
  RydeFooter,
  RydeNav,
  SectionTag,
  WhatsAppButton,
} from "@/components/ryde/Ryde";
import { createApplication, verifyPayment } from "@/lib/api";
import { trackEvent } from "@/lib/tracking";
import { getWhatsAppUrl } from "@/lib/whatsapp";

const CHIPS = ["10.1\" IPS glass", "4 GB / 64 GB", "Android 14", "4G + GPS", "Rear-glass mount", "Dual use"];

const KIT_ITEMS = [
  "Slate 01 Android tablet",
  "Inside rear-window mount (no drill)",
  "Car power lead",
  "RydeCast player preloaded",
  "RydeCast owner / driver app",
  "1-year hardware exchange",
];

const SPECS = [
  ["Display", "10.1\" IPS, 1920×1200, glass"],
  ["Body", "Slim tablet chassis ~8–9 mm"],
  ["OS", "Android 14"],
  ["Memory", "4 GB RAM + 64 GB, microSD"],
  ["Radios", "Wi-Fi, Bluetooth, GPS, 4G nano-SIM"],
  ["Power", "6000–7000 mAh + vehicle power, USB-C"],
  ["Cameras", "8 MP rear / 5 MP front (or factory equivalent)"],
  ["Mount", "Inside rear windshield, no drilling"],
  ["Software", "RydeCast player + owner app + full Android"],
  ["Warranty", "12-month exchange for hardware malfunction (not cracks, water, misuse)"],
];

const FAQS = [
  ["Is it an iPad?", "No. Slate 01 is an Android tablet with an iPad-size 10.1\" screen. It runs Android 14 with the Play Store — YouTube, Maps, Chrome, classes, video calls."],
  ["When do I get it?", "Delivery is 15–20 days from booking. The moment your unit ships, we WhatsApp you its photo, serial number, and live tracking."],
  ["Can I cancel?", "Yes. Full refund of all money paid until the dispatch week is sent to you. No questions, no cancellation fee."],
  ["What if ads are slow in my city?", "You still have the tablet. Earnings come only from live campaigns, but the hardware is yours either way — it works as a normal Android tablet."],
  ["COD?", "Yes — that's the safe way. ₹499 online locks your unit, and you pay the ₹4,500 balance in cash when the box reaches your door. You see the unit before you pay the balance."],
  ["Is the ₹15,000/month thing real?", "Earnings are never a fixed salary. In active campaign months, busy-city drivers can earn up to that range — it depends entirely on live ad inventory in your city. No campaign = no payout that month."],
];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const inputClass =
  "w-full rounded-xl border border-[#2A322E] bg-[#0B0D0C] px-4 py-3.5 text-base text-[#F4F7F5] outline-none transition-colors duration-200 placeholder:text-[#9AA8A0]/60 focus:border-[#C6FF3D] focus:ring-2 focus:ring-[#C6FF3D]/20";

function OrderForm() {
  const [form, setForm] = useState({ name: "", city: "", phone: "", upi_note: "" });
  const [plan, setPlan] = useState("booking");
  const [stage, setStage] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [order, setOrder] = useState(null);
  const [queueId, setQueueId] = useState(null);
  const [rescueUsed, setRescueUsed] = useState(false);
  const formTouched = useRef(false);

  const payable = plan === "full" ? 4999 : 499;

  const markFormStart = (field) => {
    if (formTouched.current) return;
    formTouched.current = true;
    trackEvent("form_start", field, "/");
  };

  const set = (key) => (e) => {
    markFormStart(key);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const confirmPayment = async ({ appId, orderId, paymentId, signature }) => {
    try {
      const res = await verifyPayment({ application_id: appId, order_id: orderId, payment_id: paymentId, signature });
      trackEvent("payment_success", `slate01_${plan}`, "/");
      setQueueId(res.queue_id);
      setStage("success");
    } catch (err) {
      trackEvent("payment_verify_failed", err?.response?.data?.detail || "verification_failed", "/");
      setStage("form");
      toast.error(err?.response?.data?.detail || "Payment could not be verified. WhatsApp us if money was deducted.");
    }
  };

  const launchRazorpay = (ord) => {
    const rzp = new window.Razorpay({
      key: ord.key_id,
      amount: ord.amount,
      currency: "INR",
      name: "RydeCast",
      description: plan === "full" ? "Slate 01 kit — full payment" : "Slate 01 — booking",
      order_id: ord.order_id,
      prefill: { name: form.name, contact: form.phone },
      notes: { application_id: ord.application_id },
      theme: { color: "#C6FF3D" },
      handler: (resp) =>
        confirmPayment({
          appId: ord.application_id,
          orderId: ord.order_id,
          paymentId: resp.razorpay_payment_id,
          signature: resp.razorpay_signature,
        }),
      modal: {
        ondismiss: () => {
          trackEvent("payment_dismissed", rescueUsed ? "second_dismiss" : "first_dismiss", "/");
          if (rescueUsed) {
            setStage("form");
            toast.message("Payment closed — your booking is not confirmed yet.");
          } else {
            setRescueUsed(true);
            setStage("exit_rescue");
          }
        },
      },
    });
    rzp.on("payment.failed", (resp) => {
      trackEvent("payment_failed", resp?.error?.code || "gateway_failure", "/");
      toast.error(resp?.error?.description || "Payment failed — try another card or UPI account.");
    });
    trackEvent("payment_opened", "razorpay", "/");
    rzp.open();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    trackEvent("payment_click", `slate01_${plan}`, "/");
    setSubmitting(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Razorpay failed to load. Check connection.");
      const res = await createApplication({ ...form, plan });
      trackEvent("order_created", `slate01_${plan}`, "/");
      setOrder(res);
      launchRazorpay(res);
    } catch (err) {
      trackEvent("order_failed", err?.response?.data?.detail || "create_order_failed", "/");
      toast.error(err?.response?.data?.detail || err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === "success") {
    return (
      <BentoCard className="border-[#C6FF3D]/40" data-testid="payment-success-card">
        <CheckCircle2 className="text-[#C6FF3D]" size={48} />
        <p className="mt-5 font-jbmono text-xs uppercase tracking-[0.3em] text-[#C6FF3D]">Booking confirmed</p>
        <h3 className="mt-3 font-display text-3xl font-black tracking-[-0.02em] md:text-5xl">You’re in queue.</h3>
        {queueId && (
          <p className="mt-5 inline-flex rounded-xl border border-[#C6FF3D]/40 bg-[#C6FF3D]/10 px-6 py-3 font-jbmono text-2xl font-bold tracking-[0.15em] text-[#C6FF3D]" data-testid="queue-id">
            {queueId}
          </p>
        )}
        <p className="mt-5 max-w-md leading-7 text-[#9AA8A0]">
          Your Slate 01 is locked in. Delivery in 15–20 days — we’ll WhatsApp you the photo, serial number, and live tracking the moment it ships. Track anytime with your phone number or queue ID.
        </p>
        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/track"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#C6FF3D] px-6 py-3.5 font-display font-extrabold text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] active:scale-95"
            data-testid="success-track-link"
          >
            <Truck size={18} />
            Track your order
          </Link>
          <a
            href={getWhatsAppUrl(`Namaste RydeCast, mera Slate 01 payment ho gaya hai. Queue ID: ${queueId || ""}. City: ${form.city}`)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#25D366]/40 bg-[#25D366]/10 px-6 py-3.5 font-display font-bold text-[#25D366] transition-colors duration-200 hover:bg-[#25D366]/20 active:scale-95"
            data-testid="success-whatsapp-link"
          >
            <MessageCircle size={18} />
            Confirm on WhatsApp
          </a>
        </div>
      </BentoCard>
    );
  }

  return (
    <>
      <BentoCard data-testid="order-form-card">
        <div className="flex items-center gap-3">
          <span className="rounded-xl border border-[#C6FF3D]/30 bg-[#C6FF3D]/10 p-3 text-[#C6FF3D]"><Lock size={20} /></span>
          <div>
            <h3 className="font-display text-2xl font-extrabold tracking-tight">Buy your Slate 01</h3>
            <p className="text-sm text-[#9AA8A0]">Secure Razorpay payment opens after this form.</p>
          </div>
        </div>

        <div className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="plan-select">
          <button
            type="button"
            onClick={() => setPlan("booking")}
            className={`rounded-xl border p-5 text-left transition-colors duration-200 active:scale-95 ${plan === "booking" ? "border-[#C6FF3D] bg-[#C6FF3D]/10" : "border-[#2A322E] bg-[#0B0D0C] hover:border-[#C6FF3D]/40"}`}
            data-testid="plan-booking"
          >
            <p className="font-display text-2xl font-black text-[#C6FF3D]">₹499</p>
            <p className="mt-1 font-display text-base font-bold">Lock your unit today</p>
            <p className="mt-2 text-xs leading-5 text-[#9AA8A0]">Locks your unit today. Balance ₹4,500 on delivery — prepaid or COD. Refundable until dispatch.</p>
          </button>
          <button
            type="button"
            onClick={() => setPlan("full")}
            className={`rounded-xl border p-5 text-left transition-colors duration-200 active:scale-95 ${plan === "full" ? "border-[#C6FF3D] bg-[#C6FF3D]/10" : "border-[#2A322E] bg-[#0B0D0C] hover:border-[#C6FF3D]/40"}`}
            data-testid="plan-full"
          >
            <p className="font-display text-2xl font-black text-[#C6FF3D]">₹4,999</p>
            <p className="mt-1 font-display text-base font-bold">Full payment upfront</p>
            <p className="mt-2 text-xs leading-5 text-[#9AA8A0]">Kit fully paid today — nothing left to pay at delivery. Fastest way to the front of the queue.</p>
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-6 grid gap-5" data-testid="order-form">
          <label className="block">
            <span className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">Full name</span>
            <input required minLength={3} value={form.name} onChange={set("name")} className={inputClass} placeholder="Your name" data-testid="apply-name-input" />
          </label>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">City</span>
              <input required value={form.city} onChange={set("city")} className={inputClass} placeholder="Mumbai, Delhi, Indore…" data-testid="apply-city-input" />
            </label>
            <label className="block">
              <span className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">WhatsApp number</span>
              <input
                required
                inputMode="numeric"
                pattern="[0-9]{10}"
                value={form.phone}
                onChange={(e) => {
                  markFormStart("phone");
                  setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }));
                }}
                className={inputClass}
                placeholder="10-digit mobile"
                data-testid="apply-phone-input"
              />
            </label>
          </div>
          <label className="block">
            <span className="mb-2 block font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">UPI note (optional)</span>
            <input value={form.upi_note} onChange={set("upi_note")} className={inputClass} placeholder="Anything we should know" data-testid="apply-upi-input" />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="mt-1 inline-flex min-h-[56px] w-full items-center justify-center rounded-xl bg-[#C6FF3D] px-7 py-4 font-display text-lg font-extrabold tracking-tight text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] focus:outline-none focus:ring-2 focus:ring-[#C6FF3D] focus:ring-offset-2 focus:ring-offset-[#0B0D0C] active:scale-95 disabled:opacity-60"
            data-testid="apply-pay-button"
          >
            {submitting && <Loader2 className="mr-2 animate-spin" size={18} />}
            {submitting ? "Opening secure payment…" : plan === "full" ? "Buy Now — ₹4,999" : "Buy Now — ₹499"}
          </button>
        </form>

        <div className="mt-6 grid gap-3 text-sm text-[#9AA8A0] sm:grid-cols-2" data-testid="order-trust-list">
          <p className="flex items-center gap-2"><ShieldCheck className="shrink-0 text-[#C6FF3D]" size={17} /> Razorpay secure payment</p>
          <p className="flex items-center gap-2"><BadgeCheck className="shrink-0 text-[#C6FF3D]" size={17} /> Refundable until dispatch week</p>
          <p className="flex items-center gap-2"><Truck className="shrink-0 text-[#C6FF3D]" size={17} /> Live order tracking with queue ID</p>
          <p className="flex items-center gap-2"><Package className="shrink-0 text-[#C6FF3D]" size={17} /> Photo + serial before dispatch</p>
        </div>
        <p className="mt-6 text-xs leading-5 text-[#9AA8A0]">
          By continuing you agree to the <Link to="/terms" className="font-bold text-[#C6FF3D]" data-testid="order-terms-link">Terms</Link>, <Link to="/refunds" className="font-bold text-[#C6FF3D]" data-testid="order-refunds-link">Refund Policy</Link>, and <Link to="/privacy" className="font-bold text-[#C6FF3D]" data-testid="order-privacy-link">Privacy Policy</Link>.
        </p>
      </BentoCard>

      <AnimatePresence>
        {stage === "exit_rescue" && (
          <motion.div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="checkout-exit-rescue">
            <motion.div initial={{ scale: 0.96, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 16 }} className="max-w-md rounded-2xl border border-[#2A322E] bg-[#141816] p-8">
              <h3 className="font-display text-3xl font-extrabold tracking-tight">Payment window closed?</h3>
              <p className="mt-4 text-sm leading-7 text-[#9AA8A0]">
                No money is confirmed until Razorpay completes and our server verifies it. You can safely reopen the same booking.
              </p>
              <div className="mt-7 grid gap-3">
                <button
                  className="rounded-xl bg-[#C6FF3D] px-6 py-3.5 font-display font-extrabold text-[#0B0D0C] transition-colors duration-200 hover:bg-[#D4FF6B] active:scale-95"
                  onClick={() => {
                    trackEvent("payment_rescue_reopen", "exit_rescue", "/");
                    setStage("form");
                    launchRazorpay(order);
                  }}
                  data-testid="exit-rescue-reopen-button"
                >
                  Reopen secure payment
                </button>
                <button className="rounded-xl border border-[#2A322E] px-6 py-3.5 font-display font-bold text-[#F4F7F5] transition-colors duration-200 hover:border-[#C6FF3D]/50 active:scale-95" onClick={() => setStage("form")} data-testid="exit-rescue-cancel-button">
                  I’ll pay later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function RydeHome() {
  return (
    <main className="min-h-screen bg-[#0B0D0C] font-body text-[#F4F7F5]" data-testid="home-page">
      <RydeNav />

      {/* HERO */}
      <section className="px-5 pb-20 pt-32 md:px-8 md:pt-44" data-track-section="hero">
        <div className="mx-auto grid max-w-6xl items-center gap-14 lg:grid-cols-12">
          <RydeFade className="lg:col-span-7">
            <SectionTag testId="home-hero-tag">Slate 01 · GPS-enabled Android tablet</SectionTag>
            <h1 className="mt-6 font-display text-5xl font-black leading-[0.98] tracking-[-0.03em] md:text-7xl" data-testid="home-hero-heading">
              Billboard on the road.
              <span className="block text-[#C6FF3D]">Tablet in your hands.</span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-8 text-[#9AA8A0]" data-testid="home-hero-copy">
              Install Slate 01 inside the rear windshield. RydeCast ads run while you drive. Take it off — it’s a full 10.1" Android tablet. You own the hardware.
            </p>
            <p className="mt-6 font-display text-3xl font-black" data-testid="home-hero-price">
              ₹4,999 <span className="font-jbmono text-xs font-normal uppercase tracking-[0.2em] text-[#9AA8A0]">complete kit</span>
            </p>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
              <RydeCta href="#order" testId="home-hero-cta">Buy Now — ₹499</RydeCta>
              <GhostButton href="#kit" testId="home-hero-kit-link">See the kit</GhostButton>
            </div>
            <p className="mt-6 max-w-xl text-sm leading-6 text-[#9AA8A0]" data-testid="home-hero-micro">
              You own the hardware · No drilling · 1-year exchange · Delivery in 15–20 days · COD available
            </p>
          </RydeFade>
          <RydeFade delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <img
                src="/images/hero-kit.jpg"
                alt="Slate 01 starter kit — tablet, mount, car charger, cable and quick start guide in the box"
                className="w-full rounded-2xl border border-[#2A322E] object-cover shadow-[0_30px_90px_rgba(0,0,0,0.55)]"
                data-testid="home-hero-image"
              />
              <div className="absolute -bottom-5 left-5 rounded-xl border border-[#2A322E] bg-[#0B0D0C]/95 px-5 py-3 backdrop-blur">
                <p className="font-jbmono text-[10px] uppercase tracking-[0.2em] text-[#9AA8A0]">Starter kit · COD available</p>
                <p className="font-display text-2xl font-black text-[#C6FF3D]" data-testid="home-hero-kitprice">₹4,999</p>
              </div>
            </div>
          </RydeFade>
        </div>
        <RydeFade delay={0.2}>
          <div className="mx-auto mt-14 flex max-w-6xl flex-wrap gap-2" data-testid="home-chips">
            {CHIPS.map((chip) => (
              <span key={chip} className="rounded-full border border-[#2A322E] bg-[#141816] px-4 py-2 font-jbmono text-xs text-[#9AA8A0]">
                {chip}
              </span>
            ))}
          </div>
        </RydeFade>
      </section>

      {/* TWO MODES */}
      <section id="modes" className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="modes">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-modes-tag">Two modes</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              One device. Two jobs.
            </h2>
          </RydeFade>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            <RydeFade>
              <BentoCard className="h-full" data-testid="home-mode-car">
                <img
                  src="/images/car-mode.webp"
                  alt="Slate 01 glowing inside the rear windshield of a car in evening traffic, playing an ad"
                  className="aspect-[4/3] w-full rounded-xl border border-[#2A322E] object-cover object-[center_35%]"
                  data-testid="home-mode-car-img"
                />
                <Car size={26} className="mt-6 text-[#C6FF3D]" strokeWidth={1.6} />
                <p className="mt-4 font-display text-2xl font-extrabold tracking-tight">Car mode</p>
                <p className="mt-3 text-sm leading-7 text-[#9AA8A0]">
                  Mounted inside the rear glass, the RydeCast player faces traffic. GPS + 4G report verified playtime. Runs on 12V / USB-C car power while you drive.
                </p>
              </BentoCard>
            </RydeFade>
            <RydeFade delay={0.1}>
              <BentoCard className="h-full" data-testid="home-mode-tablet">
                <div className="flex aspect-video w-full items-center justify-center rounded-xl border border-[#2A322E] bg-white p-3">
                  <img
                    src="/images/tablet-mode.webp"
                    alt="Slate 01 Android tablet — front, side and edge views with the home screen on"
                    className="h-full w-full object-contain"
                    data-testid="home-mode-tablet-img"
                  />
                </div>
                <Smartphone size={26} className="mt-6 text-[#C6FF3D]" strokeWidth={1.6} />
                <p className="mt-4 font-display text-2xl font-extrabold tracking-tight">Tablet mode</p>
                <p className="mt-3 text-sm leading-7 text-[#9AA8A0]">
                  Unclip it and it’s an everyday 10" Android slate. Play Store apps, YouTube, Maps, online classes, video calls. Ghar pe bhi kaam aata hai.
                </p>
              </BentoCard>
            </RydeFade>
          </div>
        </div>
      </section>

      {/* THE KIT */}
      <section id="kit" className="border-t border-[#2A322E] bg-[#141816]/40 px-5 py-24 md:px-8" data-track-section="kit">
        <div className="mx-auto grid max-w-6xl items-start gap-12 lg:grid-cols-12">
          <RydeFade className="lg:col-span-6">
            <SectionTag testId="home-kit-tag">The kit</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Everything in one box.
            </h2>
            <div className="mt-9 space-y-4">
              {KIT_ITEMS.map((item, i) => (
                <p key={item} className="flex items-start gap-3 text-[#F4F7F5]" data-testid={`home-kit-item-${i}`}>
                  <BadgeCheck size={19} className="mt-0.5 shrink-0 text-[#C6FF3D]" />
                  <span className="font-display text-lg font-bold">{item}</span>
                </p>
              ))}
            </div>
          </RydeFade>
          <RydeFade delay={0.12} className="lg:col-span-6">
            <img
              src="/images/kit-catalog.jpg"
              alt="Slate 01 box opened — tablet, suction mount, car charger, USB-C cable and quick start guide"
              className="aspect-square w-full rounded-2xl border border-[#2A322E] object-cover"
              data-testid="home-kit-image"
            />
          </RydeFade>
        </div>
      </section>

      {/* SPECS */}
      <section id="specs" className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="specs">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-specs-tag">Kit spec</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Numbers, not adjectives.
            </h2>
          </RydeFade>
          <RydeFade delay={0.08}>
            <BentoCard className="mt-12 p-0" data-testid="home-specs-table">
              <div className="divide-y divide-[#2A322E]">
                {SPECS.map(([label, value], i) => (
                  <div key={label} className="grid gap-1 px-6 py-4 sm:grid-cols-3 md:px-8" data-testid={`home-spec-${i}`}>
                    <p className="font-jbmono text-xs uppercase tracking-[0.18em] text-[#9AA8A0]">{label}</p>
                    <p className="font-display text-base font-bold sm:col-span-2">{value}</p>
                  </div>
                ))}
              </div>
            </BentoCard>
            <p className="mt-4 text-xs leading-5 text-[#9AA8A0]" data-testid="home-specs-note">
              Target kit spec. Confirm exact RAM / Android build / SIM on the unit you ship — we’ll share your unit’s photo and serial before dispatch.
            </p>
          </RydeFade>
        </div>
      </section>

      {/* MONEY — HONEST */}
      <section id="money" className="border-t border-[#2A322E] bg-[#141816]/40 px-5 py-24 md:px-8" data-track-section="money">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-money-tag">The money part, honestly</SectionTag>
            <h2 className="mt-5 max-w-3xl font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Ads pay when campaigns are live.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#9AA8A0]" data-testid="home-money-copy">
              Earnings come from verified playtime on live brand campaigns in your city. Paid on the 5th by UPI / bank transfer when ads actually ran. No campaign = no payout that month. The tablet is still yours.
            </p>
            <p className="mt-4 max-w-2xl border-l-2 border-[#C6FF3D] pl-5 text-sm leading-7 text-[#9AA8A0]" data-testid="home-money-upto">
              In active months, drivers in busy cities can earn up to ₹15,000 — that number depends entirely on live ad inventory, your city, and your verified hours. It is not a salary.
            </p>
            <div className="mt-8">
              <GhostButton to="/world" testId="home-money-world-link">
                See how the RydeCast network works
                <ArrowRight size={17} />
              </GhostButton>
            </div>
          </RydeFade>
        </div>
      </section>

      {/* PRICE + HOW TO ORDER */}
      <section id="pricing" className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="pricing">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-pricing-tag">Price + how to order</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              ₹4,999. Book today.
              <span className="block text-[#C6FF3D]">At your door in 15–20 days.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#9AA8A0]">
              Pay ₹499 to lock your Slate 01. We pack your unit, WhatsApp you its photo and serial number, and deliver within 15–20 days — balance ₹4,500 prepaid or cash on delivery.
            </p>
          </RydeFade>
          <div className="mt-12 grid gap-8 lg:grid-cols-12">
            <RydeFade className="lg:col-span-5">
              <div className="space-y-4" data-testid="home-order-steps">
                {[
                  ["Pay ₹499 today", "Locks your Slate 01 and your queue ID (like RC-0142). Fully refundable until dispatch."],
                  ["We pack + prove it", "Photo and serial number of your actual unit, sent to your WhatsApp."],
                  ["Delivered in 15–20 days", "Balance ₹4,500 — prepaid, or cash on delivery when the box is at your door."],
                  ["Track it live", "Your queue ID shows every step on the Track Order page."],
                ].map(([title, text], i) => (
                  <BentoCard key={title} className="flex gap-4 p-5" data-testid={`home-order-step-${i}`}>
                    <p className="font-jbmono text-sm text-[#C6FF3D]">0{i + 1}</p>
                    <div>
                      <p className="font-display text-lg font-extrabold">{title}</p>
                      <p className="mt-1 text-sm leading-6 text-[#9AA8A0]">{text}</p>
                    </div>
                  </BentoCard>
                ))}
              </div>
              <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#C6FF3D]/40 bg-[#C6FF3D]/5 p-5" data-testid="home-hard-rule">
                <ShieldCheck className="mt-0.5 shrink-0 text-[#C6FF3D]" size={19} />
                <p className="text-sm leading-6 text-[#F4F7F5]">
                  <span className="font-bold">COD promise:</span> only ₹499 online today. Pay the rest in cash when the box is in your hands — you see your unit before you pay the balance.
                </p>
              </div>
            </RydeFade>
            <RydeFade delay={0.1} className="lg:col-span-7">
              <div id="order" className="scroll-mt-28">
                <OrderForm />
              </div>
            </RydeFade>
          </div>
        </div>
      </section>

      {/* WHY THIS PRICE */}
      <section id="why-price" className="border-t border-[#2A322E] bg-[#141816]/40 px-5 py-24 md:px-8" data-track-section="why-price">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-why-tag">Why this price</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Factory batch. Our software.
              <span className="block text-[#C6FF3D]">Sold by us.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#9AA8A0]" data-testid="home-why-copy">
              Slate 01 is a factory 10" Android tablet with the RydeCast app flashed in-house. We sell direct — no mall, no extra brand tax, no chain of importers. You pay for the slate, the mount, and our software. That is why the kit is ₹4,999, not a marked-up “income machine.”
            </p>
          </RydeFade>
        </div>
      </section>

      {/* WARRANTY */}
      <section id="warranty" className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="warranty">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-warranty-tag">Warranty</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              1-year exchange. In writing.
            </h2>
          </RydeFade>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <RydeFade>
              <BentoCard className="h-full" data-testid="home-warranty-covered">
                <ShieldCheck size={24} className="text-[#C6FF3D]" />
                <p className="mt-4 font-display text-xl font-extrabold">Covered — we exchange the unit</p>
                <ul className="mt-4 space-y-2.5 text-sm leading-6 text-[#9AA8A0]">
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> Won’t power on</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> Dead screen or touch</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> Won’t charge</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> Radios dead (Wi-Fi / 4G / GPS)</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> Player won’t start on a good unit</li>
                </ul>
                <p className="mt-5 text-xs leading-5 text-[#9AA8A0]">Starts on delivery. Replacement carries the remaining term.</p>
              </BentoCard>
            </RydeFade>
            <RydeFade delay={0.1}>
              <BentoCard className="h-full" data-testid="home-warranty-not-covered">
                <CircleX size={24} className="text-[#9AA8A0]" />
                <p className="mt-4 font-display text-xl font-extrabold">Not covered</p>
                <ul className="mt-4 space-y-2.5 text-sm leading-6 text-[#9AA8A0]">
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0 text-[#9AA8A0]" /> Cracked glass or physical damage</li>
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0 text-[#9AA8A0]" /> Water damage</li>
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0 text-[#9AA8A0]" /> Bad third-party charger damage</li>
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0 text-[#9AA8A0]" /> Unofficial ROM / tampering</li>
                </ul>
              </BentoCard>
            </RydeFade>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section id="who" className="border-t border-[#2A322E] bg-[#141816]/40 px-5 py-24 md:px-8" data-track-section="who">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-who-tag">Straight up</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Who this is for. And who it isn’t.
            </h2>
          </RydeFade>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <RydeFade>
              <BentoCard className="h-full border-[#C6FF3D]/30" data-testid="home-who-for">
                <p className="font-display text-xl font-extrabold text-[#C6FF3D]">For you if…</p>
                <ul className="mt-4 space-y-3 text-sm leading-6 text-[#F4F7F5]">
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> You drive daily — commute, cab, fleet</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> You want a real Android tablet that can also join the ad network</li>
                  <li className="flex gap-2"><CheckCircle2 size={16} className="mt-1 shrink-0 text-[#C6FF3D]" /> You’re okay waiting for your batch — with photo + serial proof</li>
                </ul>
              </BentoCard>
            </RydeFade>
            <RydeFade delay={0.1}>
              <BentoCard className="h-full" data-testid="home-who-not">
                <p className="font-display text-xl font-extrabold text-[#9AA8A0]">Not for you if…</p>
                <ul className="mt-4 space-y-3 text-sm leading-6 text-[#9AA8A0]">
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0" /> You need a guaranteed monthly salary — no one honest can promise that</li>
                  <li className="flex gap-2"><CircleX size={16} className="mt-1 shrink-0" /> You expect ad earnings even when no campaigns are live in your city</li>
                </ul>
              </BentoCard>
            </RydeFade>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="faq">
        <div className="mx-auto max-w-6xl">
          <RydeFade>
            <SectionTag testId="home-faq-tag">Sawaal · Seedhe jawab</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              Ask the uncomfortable questions.
            </h2>
          </RydeFade>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {FAQS.map(([q, a], i) => (
              <RydeFade key={q} delay={i * 0.05}>
                <BentoCard className="h-full" data-testid={`home-faq-${i}`}>
                  <p className="font-display text-lg font-extrabold tracking-tight">{q}</p>
                  <p className="mt-3 text-sm leading-7 text-[#9AA8A0]">{a}</p>
                </BentoCard>
              </RydeFade>
            ))}
          </div>
          <RydeFade delay={0.1}>
            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <WhatsAppButton source="home_faq" message="I want Slate 01 booking. City:" label="WhatsApp pe poochho" testId="home-faq-whatsapp" />
              <p className="text-sm text-[#9AA8A0]">Real team. Sach baat, no bots. Ask anything before paying.</p>
            </div>
          </RydeFade>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-[#2A322E] px-5 py-24 md:px-8" data-track-section="final">
        <RydeFade>
          <div className="mx-auto max-w-6xl rounded-2xl border border-[#C6FF3D]/30 bg-[#141816] p-8 md:p-14" data-testid="home-final-card">
            <SectionTag>Next batch filling now</SectionTag>
            <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-[-0.02em] md:text-6xl">
              ₹499 today.
              <span className="block text-[#C6FF3D]">In your hands in 15–20 days.</span>
            </h2>
            <p className="mt-6 max-w-xl text-lg leading-8 text-[#9AA8A0]">
              Queue ID on payment. Photo + serial on WhatsApp. Balance on COD at your door.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <RydeCta href="#order" testId="home-final-cta">Buy Now — ₹499</RydeCta>
              <WhatsAppButton source="home_final" message="I want Slate 01 booking. City:" label="Pehle WhatsApp pe poochho" testId="home-final-whatsapp" />
            </div>
          </div>
        </RydeFade>
      </section>

      <RydeFooter />
    </main>
  );
}
